import React, { useEffect, useMemo, useState } from "react";
import adminPosApi from "../services/adminPosApi";
import adminApiClient from "../services/adminApiClient";

export default function AdminPosSalesPage() {
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [sales, setSales] = useState([]);
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [detail, setDetail] = useState(null);

  const [voidReason, setVoidReason] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundTotal, setRefundTotal] = useState("");

  const branchOptions = useMemo(() => branches || [], [branches]);

  async function loadBranches() {
    const res = await adminApiClient.get("/api/inventory/branches", { params: { limit: 200, offset: 0 } });
    if (!res.data?.ok) throw new Error("branches_load_failed");
    setBranches(res.data.branches || []);
  }

  async function loadSales() {
    setStatus({ type: "", msg: "" });
    try {
      const params = { limit: 100, offset: 0 };
      if (branchId) params.branchId = Number(branchId);
      const res = await adminPosApi.listSales(params);
      if (!res.ok) throw new Error(res.error || "sales_load_failed");
      setSales(res.sales || []);
    } catch {
      setStatus({ type: "error", msg: "Unable to load sales." });
    }
  }

  async function loadDetail(id) {
    try {
      const res = await adminPosApi.getSale(id);
      if (!res.ok) throw new Error(res.error || "detail_failed");
      setDetail(res.sale || null);
      if (res.sale?.total_amount != null) setRefundTotal(String(res.sale.total_amount));
    } catch {
      setDetail(null);
    }
  }

  useEffect(() => {
    loadBranches().catch(() => setStatus({ type: "error", msg: "Unable to load branches." }));
  }, []);

  useEffect(() => {
    loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  async function onVoidSale() {
    if (!selectedSaleId) return;
    setStatus({ type: "", msg: "" });
    try {
      const res = await adminPosApi.voidSale(selectedSaleId, { reason: voidReason || null });
      if (!res.ok) throw new Error(res.error || "void_failed");
      setStatus({ type: "success", msg: "Sale voided (stock returned)." });
      setVoidReason("");
      await loadSales();
      await loadDetail(selectedSaleId);
    } catch {
      setStatus({ type: "error", msg: "Unable to void sale." });
    }
  }

  async function onRefundSale() {
    if (!selectedSaleId) return;
    setStatus({ type: "", msg: "" });

    const total = Number(refundTotal || 0);
    if (!Number.isFinite(total) || total < 0) {
      setStatus({ type: "error", msg: "Refund total must be a valid number." });
      return;
    }

    try {
      const res = await adminPosApi.refundSale(selectedSaleId, {
        reason: refundReason || null,
        refundTotal: total,
      });
      if (!res.ok) throw new Error(res.error || "refund_failed");
      setStatus({ type: "success", msg: "Sale refunded (stock returned)." });
      setRefundReason("");
      await loadSales();
      await loadDetail(selectedSaleId);
    } catch {
      setStatus({ type: "error", msg: "Unable to refund sale." });
    }
  }

  return (
    <div className="ucx-admin-page">
      <div className="ucx-admin-header">
        <div>
          <h1 className="ucx-admin-title">POS Sales</h1>
          <div className="ucx-admin-subtitle">Review, void, and refund (Model B)</div>
        </div>
      </div>

      <div className="ucx-admin-panel">
        <div className="ucx-admin-panel-title">Filters</div>
        <div className="ucx-admin-form-grid ucx-admin-form-grid-2">
          <div className="ucx-admin-field">
            <label>Branch</label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">All branches</option>
              {branchOptions.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} (#{b.id})
                </option>
              ))}
            </select>
          </div>
          <div className="ucx-admin-field">
            <label>Status</label>
            <input value="Ready" readOnly />
          </div>
        </div>

        {status.type === "error" ? <div className="ucx-admin-status-error">{status.msg}</div> : null}
        {status.type === "success" ? <div className="ucx-admin-status-success">{status.msg}</div> : null}
      </div>

      <div className="ucx-admin-table-wrapper">
        <table className="ucx-admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Branch</th>
              <th>Shift</th>
              <th>Total</th>
              <th>Created</th>
              <th>Voided</th>
              <th>Refunded</th>
            </tr>
          </thead>
          <tbody>
            {sales.length === 0 ? (
              <tr>
                <td colSpan={7} className="ucx-admin-empty">
                  No sales found.
                </td>
              </tr>
            ) : (
              sales.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => {
                    setSelectedSaleId(s.id);
                    loadDetail(s.id);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td>{s.id}</td>
                  <td>{s.branch_id}</td>
                  <td>{s.shift_id}</td>
                  <td>{s.total_amount}</td>
                  <td>{s.created_at ? new Date(s.created_at).toLocaleString() : "-"}</td>
                  <td>{s.voided_at ? "yes" : "no"}</td>
                  <td>{s.refunded_at ? "yes" : "no"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedSaleId ? (
        <div className="ucx-admin-panel" style={{ marginTop: "1rem" }}>
          <div className="ucx-admin-panel-title">Sale Detail (#{selectedSaleId})</div>

          {detail ? (
            <>
              <div className="ucx-admin-subtitle">
                Shift #{detail.shift_id} • Terminal #{detail.terminal_id} • Location #{detail.stock_location_id ?? "auto"}
              </div>

              <div className="ucx-admin-lineitems">
                <div className="ucx-admin-lineitems-head">
                  <strong>Line Items</strong>
                </div>
                <div className="ucx-admin-table-wrapper">
                  <table className="ucx-admin-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Batch</th>
                        <th>Qty</th>
                        <th>Unit</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.items || []).map((it) => (
                        <tr key={it.id}>
                          <td>{it.product_id}</td>
                          <td>{it.batch_id ?? "-"}</td>
                          <td>{it.quantity}</td>
                          <td>{it.unit_price}</td>
                          <td>{it.line_total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="ucx-admin-form-grid ucx-admin-form-grid-2" style={{ marginTop: "0.75rem" }}>
                <div className="ucx-admin-field">
                  <label>Void reason</label>
                  <input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="Optional" />
                </div>
                <div className="ucx-admin-field" style={{ justifyContent: "flex-end" }}>
                  <button className="ucx-btn ucx-btn-outline" type="button" onClick={onVoidSale}>
                    Void Sale
                  </button>
                </div>

                <div className="ucx-admin-field">
                  <label>Refund reason</label>
                  <input value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="Optional" />
                </div>
                <div className="ucx-admin-field">
                  <label>Refund total</label>
                  <input value={refundTotal} onChange={(e) => setRefundTotal(e.target.value)} />
                </div>

                <div className="ucx-admin-form-actions">
                  <button className="ucx-btn" type="button" onClick={onRefundSale}>
                    Refund Sale
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="ucx-admin-subtitle">No detail available.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
